// Всплывающая карточка у точки клика, целиком в окне (как popShow/popFit в оболочке).
import { useEffect, useLayoutEffect, useRef, type ReactNode } from 'react';

export type Anchor = { x: number; y: number };

export function Pop({ at, title, onClose, children, width = 380 }:
  { at: Anchor; title: ReactNode; onClose: () => void; children: ReactNode; width?: number }) {
  const ref = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const fit = () => {
      el.style.maxHeight = (window.innerHeight - 16) + 'px';
      const w = el.offsetWidth || width, h = el.offsetHeight || 0;
      el.style.left = Math.max(8, Math.min(at.x, window.innerWidth - w - 8)) + 'px';
      el.style.top = Math.max(8, Math.min(at.y + 8, window.innerHeight - h - 8)) + 'px';
    };
    fit();
    // содержимое может подгрузиться позже (работы этажа, 2026-10-05) — окно росло вниз
    // за край экрана; теперь место пересчитывается при каждом изменении размера
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(fit) : null;
    ro?.observe(el);
    return () => ro?.disconnect();
  }, [at, width]);
  // кнопки «Закрыть» нет (владелец 2026-10-07): окно закрывается щелчком мимо него или Esc.
  // Щелчок внутри выпадающих списков и диалогов, которые рисуются поверх (портал), — не «мимо»
  useEffect(() => {
    const mimo = (ev: MouseEvent) => {
      const t = ev.target as Element | null;
      if (!t || !ref.current) return;
      if (ref.current.contains(t) || t.closest('.pw-pop, .wsug, .rf-sloj, .dlg, .vs-spisok')) return;
      onClose();
    };
    const esc = (ev: KeyboardEvent) => { if (ev.key === 'Escape') { ev.stopPropagation(); onClose(); } };
    // открывающий щелчок уже прошёл, но на всякий случай слушаем со следующего кадра
    const t = window.setTimeout(() => {
      document.addEventListener('mousedown', mimo);
      document.addEventListener('keydown', esc, true);
    }, 0);
    return () => { window.clearTimeout(t); document.removeEventListener('mousedown', mimo); document.removeEventListener('keydown', esc, true); };
  }, [onClose]);
  return (
    <div ref={ref} className="pw-pop" style={{ maxWidth: width, overflowY: 'auto' }}>
      {/* крестик вверху справа (владелец 2026-10-07) — вдобавок к Esc и щелчку мимо */}
      <a className="pw-pop-x" title="Закрыть (Esc)" aria-label="Закрыть" onClick={onClose}>✕</a>
      <b className="pw-pop-zag">{title}</b>
      {children}
    </div>
  );
}

/** Точка клика для карточки */
export function at(e: { clientX: number; clientY: number }): Anchor {
  return { x: e.clientX, y: e.clientY };
}
